/**
 * HOOK: useProspects
 * Gestion des prospects (soumissions de formulaires)
 * Charge toutes les soumissions avec enrichissement des relations
 */

import { useUsers } from '@/contexts/UsersContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { applyFiltersToQuery } from '../lib/supabase-filters';
import { buildSearchOrConditions, escapeLikeMethod } from '../lib/postgrestSearch';
import { fetchAllPages } from '../lib/supabase-paginate';
import { resolveProspectSortColumn } from '../lib/sort-columns';
import type { FormSubmission } from '../types/form';
import { useCRUD } from './useCRUD';
import { useSupabase } from './useSupabase';

interface UseProspectsOptions {
  /**
   * Mode de chargement
   * - 'client': Charge toutes les données au montage (défaut)
   * - 'server': Ne charge pas au montage (utiliser fetchPaginated)
   */
  mode?: 'client' | 'server';
}

export function useProspects(options: UseProspectsOptions = {}) {
  const { mode = 'client' } = options;
  const supabase = useSupabase();
  const { usersMap } = useUsers(); // ✅ Cache des utilisateurs
  const [enrichedData, setEnrichedData] = useState<FormSubmission[]>([]);

  /**
   * Champs recherchables pour la vue prospects_view
   * ✅ Utilise les noms de colonnes aplatis de la vue SQL
   * Ces champs seront utilisés pour la recherche server-side
   */
  const searchableFields = useMemo(
    () => [
      'contact_first_name',
      'contact_last_name',
      'contact_email',
      'contact_phone',
      'product_name',
      'bpi_code', // Recherche par n° BPI (ex. 9274LBPI000245) — déjà exposé par prospects_view (00021)
    ],
    []
  );

  /**
   * Enrichir les contacts avec les informations d'assigned_user
   * ✅ OPTIMISATION : Utilise le cache du UsersContext au lieu de get_user_by_id()
   * Pas de requêtes réseau = très rapide
   */
  const enrichWithAssignedUsers = useCallback(
    (submissions: FormSubmission[]) => {
      return submissions.map((submission) => {
        const assignedTo = submission.contact?.assigned_to;

        if (!assignedTo) return submission;

        // ✅ Utiliser le cache au lieu d'appeler get_user_by_id
        const assignedUser = usersMap[assignedTo];

        return {
          ...submission,
          contact: {
            ...submission.contact,
            assigned_user: assignedUser || null,
          },
        };
      });
    },
    [usersMap]
  );

  /**
   * Aplatir les données pour faciliter la recherche et les filtres
   * Ajoute des propriétés avec des clés en notation point
   */
  const flattenProspects = useCallback((submissions: any[]) => {
    return submissions.map((submission) => ({
      ...submission,
      // Aplatir les propriétés imbriquées pour la recherche
      'form.origin.label': submission.form?.origin?.label,
      'form.product.name': submission.form?.product?.name,
      'contact.first_name': submission.contact?.first_name,
      'contact.last_name': submission.contact?.last_name,
      'contact.email': submission.contact?.email,
      'contact.phone': submission.contact?.phone,
      'contact.assigned_user.full_name': submission.contact?.assigned_user?.full_name,
      'contact.last_call_at': submission.contact?.last_call_at,
    }));
  }, []);

  const crud = useCRUD<FormSubmission>('form_submissions', {
    select: `
      *,
      form:forms(
        id,
        name,
        slug,
        origin:origins(id, name, label),
        product:products(id, name)
      ),
      contact:contacts(
        id,
        first_name,
        last_name,
        email,
        phone,
        assigned_to
      )
    `,
    orderBy: { column: 'created_at', ascending: false },
    manual: mode === 'server', // Désactiver le fetch automatique en mode server
    searchableFields,
  });

  /**
   * Rafraîchir et enrichir les données
   */
  const refreshProspects = useCallback(async () => {
    const data = await crud.refresh();
    // Filtrer les form_submissions converties (sécurité supplémentaire)
    const activeProspects = data.filter((s) => !s.converted_at);
    // Enrichir et retourner (maintenant synchrone)
    const enriched = enrichWithAssignedUsers(activeProspects);
    const flattened = flattenProspects(enriched);
    setEnrichedData(flattened);
    return flattened;
  }, [crud, enrichWithAssignedUsers, flattenProspects]);

  // Enrichir les données quand elles changent
  useEffect(() => {
    if (crud.data.length > 0) {
      // Filtrer les form_submissions converties (sécurité supplémentaire)
      const activeProspects = crud.data.filter((s) => !s.converted_at);
      // Maintenant synchrone, pas besoin d'async
      const enriched = enrichWithAssignedUsers(activeProspects);
      const flattened = flattenProspects(enriched);
      setEnrichedData(flattened);
    } else {
      setEnrichedData([]);
    }
  }, [crud.data, enrichWithAssignedUsers, flattenProspects]);

  /**
   * Wrapper pour remove avec rafraîchissement automatique
   */
  const removeWithRefresh = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await crud.remove(id);
      if (result) {
        await refreshProspects();
      }
      return result;
    },
    [crud, refreshProspects]
  );

  /**
   * Wrapper pour removeMany avec rafraîchissement automatique
   */
  const removeManyWithRefresh = useCallback(
    async (ids: string[]): Promise<boolean> => {
      const result = await crud.removeMany(ids);
      if (result) {
        await refreshProspects();
      }
      return result;
    },
    [crud, refreshProspects]
  );

  /**
   * Créer un prospect avec contact automatique
   * Compare TOUS les champs (email, first_name, last_name) pour détecter les doublons
   * Les champs vides sont comparés : vide = vide (match), vide ≠ renseigné (nouveau contact)
   * Si un champ diverge, crée un nouveau contact
   * Associe le produit du formulaire au contact
   */
  const create = useCallback(
    async (formData: {
      form_id: string;
      contact: {
        first_name: string;
        last_name: string;
        email: string;
        phone?: string;
        origin_id?: string;
        notes?: string;
      };
    }): Promise<FormSubmission | null> => {
      try {
        // 1. Chercher un contact existant où TOUS les champs correspondent (vide = vide ou valeur = valeur)
        // Récupérer tous les contacts avec le même email. ilike sans joker =
        // égalité insensible à la casse, alignée sur la clé DB lower(btrim()) —
        // un .eq() raterait `Jean.Dupont@Gmail.com` et l'INSERT tomberait sur
        // le garde triple (23505).
        const { data: existingContacts, error: searchError } = await supabase
          .from('contacts')
          .select('id, first_name, last_name')
          .ilike('email', escapeLikeMethod(formData.contact.email.trim()));

        if (searchError) {
          console.error('Error searching contact:', searchError);
          throw searchError;
        }

        // Normalisation alignée sur la clé DB (lower(btrim(...))).
        const normalizeValue = (value: string | null | undefined) =>
          (value ?? '').trim().toLowerCase();

        // Dédup = CLÉ TRIPLE (email + prénom + nom). Deux contacts ne sont le
        // MÊME contact que si les trois correspondent après normalisation. Un
        // email partagé (pilote/copilote) avec des noms différents → deux
        // contacts distincts. On ne rattache donc QUE sur un match triple ; en
        // l'absence de match on crée un nouveau contact (le garde DB triple le
        // laisse passer). Plus de fallback « n'importe quel contact du même
        // email » — c'est lui qui écrasait le copilote sur un BPI duo.
        const existingContact = existingContacts?.find((contact) => {
          const existingFirstName = normalizeValue(contact.first_name);
          const newFirstName = normalizeValue(formData.contact.first_name);
          const existingLastName = normalizeValue(contact.last_name);
          const newLastName = normalizeValue(formData.contact.last_name);

          return existingFirstName === newFirstName && existingLastName === newLastName;
        });

        let contactId: string;

        if (existingContact) {
          // Contact existe avec les champs renseignés identiques: utiliser l'ID existant
          contactId = existingContact.id;
        } else {
          // Aucun contact correspondant: créer un nouveau contact
          const { data: newContact, error: createError } = await supabase
            .from('contacts')
            .insert({
              first_name: formData.contact.first_name,
              last_name: formData.contact.last_name,
              email: formData.contact.email,
              phone: formData.contact.phone || null,
              origin_id: formData.contact.origin_id || null,
              notes: formData.contact.notes || null,
              status: 'prospect',
            })
            .select('id')
            .single();

          if (createError) {
            console.error('Error creating contact:', createError);
            throw createError;
          }

          contactId = newContact.id;
        }

        // 2. Récupérer le produit du formulaire et ses champs
        const { data: form, error: formError } = await supabase
          .from('forms')
          .select('product_id')
          .eq('id', formData.form_id)
          .single();

        if (formError) {
          console.error('Error fetching form:', formError);
        }

        // 3. Récupérer les champs du formulaire avec leur mapping
        const { data: formFields, error: fieldsError } = await supabase
          .from('form_fields')
          .select(
            `
            id,
            field_template:field_templates(
              id,
              contact_field_map
            )
          `
          )
          .eq('form_id', formData.form_id);

        if (fieldsError) {
          console.error('Error fetching form fields:', fieldsError);
        }

        // 4. Associer le produit du formulaire au contact (si disponible)
        if (form?.product_id) {
          const { error: productError } = await supabase
            .from('contact_products')
            .insert({
              contact_id: contactId,
              product_id: form.product_id,
            })
            .select();

          // Ignorer les erreurs de conflit (produit déjà associé)
          if (productError && !productError.message.includes('duplicate')) {
            console.error('Error associating product:', productError);
          }
        }

        // 5. Créer les données de soumission en utilisant les IDs des champs du formulaire
        const submissionData: Record<string, any> = {};

        // Mapper les données du contact aux IDs des champs du formulaire
        formFields?.forEach((formField: any) => {
          const mapping = formField.field_template?.contact_field_map;

          if (mapping === 'first_name') {
            submissionData[formField.id] = formData.contact.first_name;
          } else if (mapping === 'last_name') {
            submissionData[formField.id] = formData.contact.last_name;
          } else if (mapping === 'email') {
            submissionData[formField.id] = formData.contact.email;
          } else if (mapping === 'phone' && formData.contact.phone) {
            submissionData[formField.id] = formData.contact.phone;
          }
        });

        // 6. Créer la soumission de formulaire
        const { data: submission, error: submissionError } = await supabase
          .from('form_submissions')
          .insert({
            form_id: formData.form_id,
            contact_id: contactId,
            data: submissionData,
          })
          .select(
            `
            *,
            form:forms(
              id,
              name,
              slug,
              origin:origins(id, name, label),
              product:products(id, name)
            ),
            contact:contacts(
              id,
              first_name,
              last_name,
              email,
              phone,
              assigned_to
            )
          `
          )
          .single();

        if (submissionError) {
          console.error('Error creating submission:', submissionError);
          throw submissionError;
        }

        // 7. Rafraîchir les données
        await refreshProspects();

        return submission as FormSubmission;
      } catch (error) {
        console.error('Error creating prospect:', error);
        return null;
      }
    },
    [supabase, refreshProspects]
  );

  /**
   * Fetch paginé custom utilisant la vue prospects_view
   * pour permettre le filtrage server-side sur les champs de relations
   */
  const fetchPaginatedFromView = useCallback(
    async (params: any) => {
      try {
        const {
          page = 1,
          limit = 10,
          search = '',
          sort,
          filters,
          filterMode = 'and',
          requiredFilters,
        } = params;

        // Mapper les noms de champs pour la vue aplatie
        const mappedFilters = filters?.map((filter: any) => ({
          ...filter,
          field: filter.field
            .replace('contact.first_name', 'contact_first_name')
            .replace('contact.last_name', 'contact_last_name')
            .replace('contact.email', 'contact_email')
            .replace('contact.phone', 'contact_phone')
            .replace('contact.assigned_to', 'contact_assigned_to')
            .replace('form.origin.label', 'origin_label')
            .replace('form.product.name', 'product_name')
            .replace('form.generates_bpi', 'form_generates_bpi'),
        }));

        // Construire la query sur la vue
        let query = supabase
          .from('prospects_view')
          .select('*', { count: 'exact', head: false })
          .or('payment_status.neq.paid,payment_status.is.null');

        // Recherche
        // ✅ Les searchableFields sont déjà au format de la vue (colonnes aplaties)
        if (search && searchableFields && searchableFields.length > 0) {
          query = query.or(buildSearchOrConditions(searchableFields as string[], search));
        }

        // Mapper aussi les requiredFilters
        const mappedRequiredFilters = requiredFilters?.map((filter: any) => ({
          ...filter,
          field: filter.field
            .replace('contact.first_name', 'contact_first_name')
            .replace('contact.last_name', 'contact_last_name')
            .replace('contact.email', 'contact_email')
            .replace('contact.phone', 'contact_phone')
            .replace('contact.assigned_to', 'contact_assigned_to')
            .replace('form.origin.label', 'origin_label')
            .replace('form.product.name', 'product_name')
            .replace('form.generates_bpi', 'form_generates_bpi'),
        }));

        // Appliquer les filtres mappés avec le mode ET/OU
        // Les requiredFilters s'appliquent toujours en mode AND
        if (
          (mappedFilters && mappedFilters.length > 0) ||
          (mappedRequiredFilters && mappedRequiredFilters.length > 0)
        ) {
          query = applyFiltersToQuery(
            query,
            mappedFilters || [],
            filterMode,
            mappedRequiredFilters || []
          );
        }

        // Tri — les colonnes prospects exposent des ids UI francais (nom,
        // origine...) qui ne sont pas des colonnes de prospects_view :
        // resolveProspectSortColumn mappe l'id vers la vraie colonne SQL
        // (nom -> contact_last_name...) puis whiteliste (fallback created_at),
        // evitant le 400 PostgREST tout en triant sur la bonne colonne.
        const sortColumn = resolveProspectSortColumn(sort?.field);
        const sortAscending = sort?.direction === 'asc';
        query = query.order(sortColumn, { ascending: sortAscending });

        // Pagination
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);

        // Exécution
        const { data: rawData, error, count } = await query;

        if (error) throw error;

        // Reconstruire les objets imbriqués pour compatibilité
        const transformedData = (rawData || []).map((row: any) => ({
          id: row.id,
          form_id: row.form_id,
          contact_id: row.contact_id,
          data: row.data,
          bpi_code: row.bpi_code, // Code BPI si généré
          payment_status: row.payment_status,
          created_at: row.created_at,
          form: {
            id: row.form_id,
            name: row.form_name,
            slug: row.form_slug,
            origin: {
              id: row.origin_id,
              name: row.origin_name,
              label: row.origin_label,
            },
            product: {
              id: row.product_id,
              name: row.product_name,
            },
          },
          contact: {
            id: row.contact_id,
            first_name: row.contact_first_name,
            last_name: row.contact_last_name,
            email: row.contact_email,
            phone: row.contact_phone,
            assigned_to: row.contact_assigned_to,
            call_count: row.contact_call_count || 0, // Nombre d'appels du contact
          },
        })) as FormSubmission[];

        // Enrichir avec assigned_user
        const enriched = enrichWithAssignedUsers(transformedData);
        const flattened = flattenProspects(enriched);

        return {
          data: flattened,
          total: count || 0,
          page,
          limit,
          totalPages: Math.ceil((count || 0) / limit),
        };
      } catch (err) {
        console.error('[fetchPaginatedFromView] Error:', err);
        return { data: [], total: 0, page: 1, limit: 10, totalPages: 0 };
      }
    },
    [supabase, searchableFields, enrichWithAssignedUsers, flattenProspects]
  );

  /**
   * Marque un prospect comme converti (soft delete)
   * Le contact et les données de formulaire restent accessibles
   */
  const deleteProspect = useCallback(
    async (prospectId: string): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from('form_submissions')
          .update({ converted_at: new Date().toISOString() })
          .eq('id', prospectId);

        if (error) {
          console.error('Error converting prospect:', error);
          return false;
        }

        // Rafraîchir les données
        await refreshProspects();
        return true;
      } catch (err) {
        console.error('Error deleting prospect:', err);
        return false;
      }
    },
    [supabase, refreshProspects]
  );

  /**
   * Marque le prospect d'un contact comme converti (soft delete)
   * Utilisé quand un contact est ajouté à un équipage
   * Les données de formulaire sont conservées et accessibles sur la fiche contact
   * @param contactId - ID du contact
   * @param productId - ID du produit (celui de l'équipage)
   * @returns true si un prospect a été marqué comme converti, false sinon
   */
  const deleteProspectByContactAndProduct = useCallback(
    async (contactId: string, productId: string): Promise<boolean> => {
      try {
        // Chercher les form_submissions de ce contact
        // dont le formulaire a le même product_id et qui ne sont pas encore converties
        const { data: prospects, error: fetchError } = await supabase
          .from('form_submissions')
          .select(
            `
            id,
            form:forms!inner(product_id)
          `
          )
          .eq('contact_id', contactId)
          .eq('form.product_id', productId)
          .is('converted_at', null);

        if (fetchError) {
          console.error('Error fetching prospect to convert:', fetchError);
          return false;
        }

        // Si aucun prospect trouvé, c'est OK (pas d'erreur)
        if (!prospects || prospects.length === 0) {
          console.warn(
            '[deleteProspectByContactAndProduct] No prospect found for contact/product',
            {
              contactId,
              productId,
            }
          );
          return false;
        }

        // Marquer le(s) prospect(s) comme converti(s) (soft delete)
        const prospectIds = prospects.map((p) => p.id);
        const { error: updateError } = await supabase
          .from('form_submissions')
          .update({ converted_at: new Date().toISOString() })
          .in('id', prospectIds);

        if (updateError) {
          console.error('Error converting prospect:', updateError);
          return false;
        }

        return true;
      } catch (err) {
        console.error('Error in deleteProspectByContactAndProduct:', err);
        return false;
      }
    },
    [supabase]
  );

  /**
   * Récupérer TOUTES les données correspondant aux filtres (sans pagination)
   * Utilisé pour l'export CSV des données filtrées ou sélectionnées
   */
  const fetchFiltered = useCallback(
    async (params: import('../types/crud').FetchFilteredParams): Promise<FormSubmission[]> => {
      try {
        const {
          search = '',
          filters,
          filterMode = 'and',
          requiredFilters,
          ids: requestedIds,
        } = params;

        let rawData: any[];

        // ✅ Si des IDs spécifiques sont demandés (export sélection multi-pages)
        if (requestedIds && requestedIds.length > 0) {
          const { data, error } = await supabase
            .from('prospects_view')
            .select('*')
            .in('id', requestedIds);

          if (error) throw error;
          rawData = data || [];
        } else {
          // Mapper les noms de champs pour la vue aplatie
          const mappedFilters = filters?.map((filter: any) => ({
            ...filter,
            field: filter.field
              .replace('contact.first_name', 'contact_first_name')
              .replace('contact.last_name', 'contact_last_name')
              .replace('contact.email', 'contact_email')
              .replace('contact.phone', 'contact_phone')
              .replace('contact.assigned_to', 'contact_assigned_to')
              .replace('form.origin.label', 'origin_label')
              .replace('form.product.name', 'product_name')
              .replace('form.generates_bpi', 'form_generates_bpi'),
          }));

          // Mapper aussi les requiredFilters
          const mappedRequiredFilters = requiredFilters?.map((filter: any) => ({
            ...filter,
            field: filter.field
              .replace('contact.first_name', 'contact_first_name')
              .replace('contact.last_name', 'contact_last_name')
              .replace('contact.email', 'contact_email')
              .replace('contact.phone', 'contact_phone')
              .replace('contact.assigned_to', 'contact_assigned_to')
              .replace('form.origin.label', 'origin_label')
              .replace('form.product.name', 'product_name')
              .replace('form.generates_bpi', 'form_generates_bpi'),
          }));

          // R15 : factory de query (réinstancie à chaque page pour fetchAllPages)
          const buildQuery = () => {
            let query = supabase
              .from('prospects_view')
              .select('*')
              .or('payment_status.neq.paid,payment_status.is.null');

            // Recherche
            if (search && searchableFields && searchableFields.length > 0) {
              query = query.or(buildSearchOrConditions(searchableFields as string[], search));
            }

            // Appliquer les filtres
            if (
              (mappedFilters && mappedFilters.length > 0) ||
              (mappedRequiredFilters && mappedRequiredFilters.length > 0)
            ) {
              query = applyFiltersToQuery(
                query,
                mappedFilters || [],
                filterMode,
                mappedRequiredFilters || []
              );
            }

            // Tri par défaut
            return query.order('created_at', { ascending: false });
          };

          // R15 : pagination automatique pour contourner le cap PostgREST 1000
          const { data, error } = await fetchAllPages<any>(buildQuery);
          if (error) throw error;
          rawData = data || [];
        }

        // Transformer les données (même format que fetchPaginatedFromView)
        const transformedData = rawData.map((row: any) => ({
          id: row.id,
          form_id: row.form_id,
          contact_id: row.contact_id,
          data: row.data || {},
          bpi_code: row.bpi_code,
          payment_status: row.payment_status,
          created_at: row.created_at,
          form: {
            id: row.form_id,
            name: row.form_name,
            slug: row.form_slug,
            origin: {
              id: row.origin_id,
              name: row.origin_name,
              label: row.origin_label,
            },
            product: {
              id: row.product_id,
              name: row.product_name,
            },
          },
          contact: {
            id: row.contact_id,
            first_name: row.contact_first_name,
            last_name: row.contact_last_name,
            email: row.contact_email,
            phone: row.contact_phone,
            assigned_to: row.contact_assigned_to,
            call_count: row.contact_call_count || 0,
          },
        })) as FormSubmission[];

        // Enrichir avec assigned_user
        const enriched = enrichWithAssignedUsers(transformedData);
        const flattened = flattenProspects(enriched);

        return flattened;
      } catch (err) {
        console.error('[fetchFiltered] Error:', err);
        return [];
      }
    },
    [supabase, searchableFields, enrichWithAssignedUsers, flattenProspects]
  );

  /**
   * Récupérer tous les IDs correspondant aux filtres actuels (sans pagination)
   * Utilisé pour "Sélectionner tous les résultats" (Feature 4.4)
   */
  const fetchAllFilteredIds = useCallback(
    async (params: import('../types/crud').FetchFilteredParams): Promise<string[]> => {
      try {
        const { search = '', filters, filterMode = 'and', requiredFilters } = params;

        // Mapper les noms de champs pour la vue aplatie
        const mappedFilters = filters?.map((filter: any) => ({
          ...filter,
          field: filter.field
            .replace('contact.first_name', 'contact_first_name')
            .replace('contact.last_name', 'contact_last_name')
            .replace('contact.email', 'contact_email')
            .replace('contact.phone', 'contact_phone')
            .replace('contact.assigned_to', 'contact_assigned_to')
            .replace('form.origin.label', 'origin_label')
            .replace('form.product.name', 'product_name')
            .replace('form.generates_bpi', 'form_generates_bpi'),
        }));

        // Mapper aussi les requiredFilters
        const mappedRequiredFilters = requiredFilters?.map((filter: any) => ({
          ...filter,
          field: filter.field
            .replace('contact.first_name', 'contact_first_name')
            .replace('contact.last_name', 'contact_last_name')
            .replace('contact.email', 'contact_email')
            .replace('contact.phone', 'contact_phone')
            .replace('contact.assigned_to', 'contact_assigned_to')
            .replace('form.origin.label', 'origin_label')
            .replace('form.product.name', 'product_name')
            .replace('form.generates_bpi', 'form_generates_bpi'),
        }));

        // R15 : factory de query (réinstancie à chaque page pour fetchAllPages).
        // Récupère TOUS les IDs via pagination PostgREST (cap implicite = 1000) ;
        // sans cette boucle .range(), la sélection globale était silencieusement
        // tronquée à 1000 IDs (parité avec fix useContacts.fetchAllFilteredIds).
        const buildQuery = () => {
          let query = supabase
            .from('prospects_view')
            .select('id')
            .or('payment_status.neq.paid,payment_status.is.null');

          // Recherche
          if (search && searchableFields && searchableFields.length > 0) {
            query = query.or(buildSearchOrConditions(searchableFields as string[], search));
          }

          // Appliquer les filtres
          if (
            (mappedFilters && mappedFilters.length > 0) ||
            (mappedRequiredFilters && mappedRequiredFilters.length > 0)
          ) {
            query = applyFiltersToQuery(
              query,
              mappedFilters || [],
              filterMode,
              mappedRequiredFilters || []
            );
          }

          return query;
        };

        const { data, error } = await fetchAllPages<{ id: string }>(buildQuery);

        if (error) throw error;

        return data?.map((item) => item.id) || [];
      } catch (err) {
        console.error('[fetchAllFilteredIds] Error:', err);
        return [];
      }
    },
    [supabase, searchableFields]
  );

  return {
    data: enrichedData,
    allData: crud.data, // Toutes les données brutes (pour Import CSV en mode server)
    loading: crud.loading,
    error: crud.error,
    refresh: async () => {
      const data = await refreshProspects();
      return data;
    },
    refreshAll: crud.refresh, // Retourne les données brutes
    remove: removeWithRefresh,
    removeMany: removeManyWithRefresh,
    update: crud.update,
    create,
    deleteProspect,
    deleteProspectByContactAndProduct, // ✅ Supprime le prospect par contact + produit
    fetchPaginated: fetchPaginatedFromView,
    fetchFiltered, // ✅ Pour export CSV des données filtrées/sélectionnées
    fetchAllFilteredIds, // ✅ Pour sélection globale (Feature 4.4)
  };
}
